import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, Mail } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { ReportForm } from "../../components/report/reports";
import type { Dispatch } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PolygrafLogo } from "@/components/icons/logo";

type Note = { severity: "warning" | "severe"; text: string };
type QuizAnswer = {
  label: string;
  score: number;
  keyFindings?: Array<Note>;
  nextSteps?: Array<Note>;
};

export const Route = createFileRoute("/compliance-test/quiz")({
  component: RouteComponent,
});

const quiz: Array<{
  question: string;
  answers: Array<QuizAnswer>;
}> = [
  {
    question: "Does your organization have a formal AI usage policy?",
    answers: [
      {
        label: "Yes, we have a comprehensive policy",
        score: 0,
      },
      {
        label: "In progress, policy is being developed",
        score: 30,
        keyFindings: [
          {
            severity: "warning",
            text: "AI policy development in progress - needs completion",
          },
        ],
      },
      {
        label: "No, we don't have a policy yet",
        score: 60,
        keyFindings: [
          {
            severity: "severe",
            text: "No formal AI policy - significant governance gap",
          },
        ],
        nextSteps: [
          { severity: "severe", text: "Define a formal AI usage policy" },
        ],
      },
    ],
  },
  {
    question: "Who oversees AI usage in your organization?",
    answers: [
      {
        label: "We have a dedicated AI governance team",
        score: 0,
      },
      {
        label: "Responsibility is shared across departments",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Shared AI oversight - may lack clear accountability",
          },
        ],
      },
      {
        label: "No one specifically oversees AI usage",
        score: 50,
        keyFindings: [
          {
            severity: "severe",
            text: "No AI oversight - critical control gap",
          },
        ],
        nextSteps: [
          {
            severity: "severe",
            text: "Assign someone to oversee AI usage",
          },
        ],
      },
    ],
  },
  {
    question: "Are employees trained on AI ethics and compliance?",
    answers: [
      {
        label: "Yes, regular training for all employees",
        score: 0,
      },
      {
        label: "Only technical teams receive training",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Limited AI training - non-technical staff at risk",
          },
        ],
      },
      {
        label: "No formal training program yet",
        score: 40,
        keyFindings: [
          {
            severity: "warning",
            text: "No AI compliance training - employees may not understand risks",
          },
        ],
        nextSteps: [
          {
            severity: "warning",
            text: "Train employees on AI ethics and data protection requirements",
          },
        ],
      },
    ],
  },
  {
    question:
      "Do employees use external AI tools (ChatGPT, Claude, etc.) for work tasks?",
    answers: [
      {
        label: "Never, we use only internal tools",
        score: 0,
      },
      {
        label: "Occasionally with permission",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Some external AI use - monitor for data exposure",
          },
        ],
      },
      {
        label: "Often, without supervision",
        score: 50,
        keyFindings: [
          {
            severity: "severe",
            text: "Uncontrolled external AI usage - shadow AI risk detected",
          },
        ],
      },
    ],
  },
  {
    question: "Do you monitor what data employees send to AI systems?",
    answers: [
      {
        label: "Yes, we have monitoring systems in place",
        score: 0,
      },
      {
        label: "Partial monitoring only",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Incomplete data monitoring - gaps in visibility",
          },
        ],
      },
      {
        label: "No, we don't monitor AI inputs",
        score: 50,
        keyFindings: [
          {
            severity: "severe",
            text: "No AI data monitoring - blind spot for sensitive information",
          },
        ],
        nextSteps: [
          {
            severity: "severe",
            text: "Implement monitoring for AI prompt data to ensure compliance",
          },
        ],
      },
    ],
  },
  {
    question:
      "Have there been incidents of sensitive data being used with AI tools?",
    answers: [
      {
        label: "No incidents reported",
        score: 0,
      },
      {
        label: "Some concerns or close calls",
        score: 30,
        keyFindings: [
          {
            severity: "warning",
            text: "Near-miss incidents with sensitive data and AI",
          },
        ],
      },
      {
        label: "Yes, confirmed exposure incidents",
        score: 60,
        keyFindings: [
          {
            severity: "severe",
            text: "Confirmed data exposure via AI tools - serious breach risk",
          },
        ],
      },
    ],
  },
  {
    question: "Do you use AI APIs or models connected to external services?",
    answers: [
      {
        label: "Only internal/on-premise AI",
        score: 10,
        keyFindings: [
          {
            severity: "warning",
            text: "Minimal external AI exposure",
          },
        ],
      },
      {
        label: "External APIs with governance",
        score: 30,
        keyFindings: [
          {
            severity: "warning",
            text: "External AI use with some controls",
          },
        ],
      },
      {
        label: "External APIs without restrictions",
        score: 60,
        keyFindings: [
          {
            severity: "severe",
            text: "Unrestricted external AI API access - data leakage risk",
          },
        ],
      },
    ],
  },
  {
    question:
      "Are AI-generated outputs reviewed for compliance before deployment?",
    answers: [
      {
        label: "Always reviewed",
        score: 0,
      },
      {
        label: "Sometimes reviewed",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Inconsistent AI output review - quality control gaps",
          },
        ],
      },
      {
        label: "Rarely or never reviewed",
        score: 50,
        keyFindings: [
          {
            severity: "severe",
            text: "No AI output validation - compliance and accuracy risks",
          },
        ],
      },
    ],
  },
  {
    question: "Do you use AI models trained on company or customer data?",
    answers: [
      {
        label: "NO",
        score: 0,
      },
      {
        label: "Yes, with legal review",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Custom AI training with legal oversight",
          },
        ],
      },
      {
        label: "Yes, without legal review",
        score: 50,
        keyFindings: [
          {
            severity: "severe",
            text: "Unreviewed AI training on company data - compliance risk",
          },
        ],
      },
    ],
  },
  {
    question: "Do you actively track AI regulations (EU AI Act, etc.)?",
    answers: [
      {
        label: "Yes, we monitor regulations closely",
        score: 0,
      },
      {
        label: "Occasionally check updates",
        score: 20,
        keyFindings: [
          {
            severity: "warning",
            text: "Limited regulatory awareness - may miss changes",
          },
        ],
      },
      {
        label: "Not actively tracking",
        score: 40,
        keyFindings: [
          {
            severity: "warning",
            text: "No regulatory tracking - compliance blind spots likely",
          },
        ],
      },
    ],
  },
];

const maxRisk = quiz.reduce<number>(
  (sum, q) => sum + Math.max(...q.answers.map((a) => a.score)),
  0,
);

const severityThemes: Record<
  Note["severity"],
  {
    badge: string;
    border: string;
    iconWrapper: string;
    label: string;
    title: string;
  }
> = {
  warning: {
    badge:
      "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100",
    border:
      "border-amber-100 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
    iconWrapper:
      "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-100",
    label: "text-amber-700 dark:text-amber-200",
    title: "Warning",
  },
  severe: {
    badge: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-100",
    border:
      "border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10",
    iconWrapper: "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-100",
    label: "text-red-700 dark:text-red-200",
    title: "Critical",
  },
};

const riskLevels = [
  {
    min: 70,
    label: "High Risk",
    badgeClass: "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-100",
    surfaceClass:
      "border-red-200 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10",
    description:
      "Your organization shows signs of uncontrolled AI use and limited oversight. Immediate action is recommended to avoid compliance issues.",
  },
  {
    min: 40,
    label: "Medium Risk",
    badgeClass:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100",
    surfaceClass:
      "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
    description:
      "Your organization has moderate exposure to AI risks. Formalizing governance processes will significantly reduce your risk.",
  },
  {
    min: 0,
    label: "Low Risk",
    badgeClass:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-100",
    surfaceClass:
      "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    description:
      "You're in good shape, but continuous monitoring and improvement are key to maintaining compliance.",
  },
] as const;

type RiskMeta = (typeof riskLevels)[number];

const getRiskMeta = (percent: number): RiskMeta => {
  return riskLevels.find((level) => percent >= level.min) ?? riskLevels.at(-1)!;
};

function RouteComponent() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<
    number | null
  >(0);
  const [selectedAnswers, setSelectedIndices] = useState<Array<number | null>>(
    () => Array(quiz.length).fill(null),
  );

  const progress = Math.round(
    ((currentQuestionIndex ?? quiz.length) / quiz.length) * 100,
  );

  const summary = useMemo(() => {
    const s = {
      score: 0,
      goodPercent: 0,
      riskPercent: 0,
      keyFindings: [] as Array<Note>,
      nextSteps: [] as Array<Note>,
    };
    selectedAnswers.forEach((a, i) => {
      if (a == null) return;
      const answer = quiz[i].answers[a];
      s.score += answer.score;
      answer.keyFindings?.forEach((f) => s.keyFindings.push(f));
      answer.nextSteps?.forEach((f) => s.nextSteps.push(f));
    });
    s.nextSteps.push({
      severity: "severe",
      text: "Use Polygraf AI to govern and secure all AI usage across your organization",
    });
    if (maxRisk === 0) {
      s.goodPercent = 100;
      s.riskPercent = 0;
    } else {
      s.riskPercent = Math.min(100, Math.round((s.score / maxRisk) * 100));
      s.goodPercent = Math.max(0, 100 - s.riskPercent);
    }
    return s;
  }, [selectedAnswers]);

  const riskPercent = summary.riskPercent;
  const riskMeta = getRiskMeta(riskPercent);
  const findingsCount = summary.keyFindings.length;
  const [isReportDialogOpen, setReportDialogOpen] = useState(false);

  return (
    <div className="mx-auto flex min-h-screen max-w-4xl items-center justify-center px-6 py-8">
      {currentQuestionIndex !== null ? (
        <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between text-sm font-medium text-muted-foreground">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>
          <QuizPage
            key={currentQuestionIndex}
            {...quiz[currentQuestionIndex]}
            selectedAnswer={selectedAnswers[currentQuestionIndex]}
            setSelectedAnswer={(i) => {
              return setSelectedIndices((old) => {
                old[currentQuestionIndex] = i;
                return [...old];
              });
            }}
          />
          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() =>
                setCurrentQuestionIndex((i) => {
                  if (i === null) return null;
                  if (i === 0) return 0;
                  else return i - 1;
                })
              }
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                setCurrentQuestionIndex((i) => {
                  if (i === null) return null;
                  if (i < quiz.length - 1) return i + 1;
                  else return null;
                })
              }
              disabled={selectedAnswers[currentQuestionIndex] === null}
            >
              Next
            </Button>
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <span className="mr-1">🔒</span>Your answers are confidential
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="flex justify-center">
            <a
              href="https://polygraf.ai"
              target="_blank"
              rel="noreferrer"
              className="inline-flex"
            >
              <PolygrafLogo variant="lg" className="h-11 w-auto" />
            </a>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-semibold tracking-wide text-foreground uppercase">
              Your AI Compliance Assessment
            </h2>
            <p className="mt-2 text-sm font-semibold text-muted-foreground">
              Based on your responses, here&apos;s your organization&apos;s risk
              profile
            </p>
          </div>

          <div
            className={cn(
              "rounded-3xl border p-8 text-center shadow-sm sm:p-10",
              riskMeta.surfaceClass,
            )}
          >
            <div className="mx-auto flex max-w-md flex-col items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/80 text-destructive dark:bg-slate-900">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Your AI Risk Score
              </p>
              <div className="text-6xl font-bold">{riskPercent}%</div>
              <span
                className={cn(
                  "rounded-full px-4 py-1 text-sm font-semibold",
                  riskMeta.badgeClass,
                )}
              >
                {riskMeta.label}
              </span>
              <p className="text-base text-muted-foreground">
                {riskMeta.description}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm font-semibold text-muted-foreground">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Key Findings from Your Assessment</span>
              </div>
              <span className="text-xs tracking-wide uppercase">
                {findingsCount} {findingsCount === 1 ? "finding" : "findings"}
              </span>
            </div>
            {findingsCount ? (
              <FindingsWithBlur
                findings={summary.keyFindings}
                summary={summary}
                onRequestReport={() => setReportDialogOpen(true)}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No key findings were triggered. Keep reinforcing your current
                policies.
              </p>
            )}
          </div>
          <Dialog open={isReportDialogOpen} onOpenChange={setReportDialogOpen}>
            <DialogContent className="max-w-3xl overflow-y-auto p-0">
              <DialogHeader className="sr-only">
                <DialogTitle>Request AI Risk Report</DialogTitle>
              </DialogHeader>
              <ReportForm
                summary={summary}
                className="max-h-[80vh] overflow-y-auto rounded-none border-0 shadow-none sm:rounded-2xl dark:bg-[#18181b]"
                onBack={() => setReportDialogOpen(false)}
                onSubmitSuccess={() => setReportDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}

function QuizPage({
  question,
  answers,
  selectedAnswer,
  setSelectedAnswer,
}: {
  question: string;
  answers: Array<QuizAnswer>;
  selectedAnswer: number | null;
  setSelectedAnswer: Dispatch<number>;
}) {
  const reactId = useId();
  return (
    <>
      <h3 className="mb-6 text-2xl font-semibold tracking-tight sm:text-3xl">
        {question}
      </h3>

      <RadioGroup
        className="space-y-4"
        value={selectedAnswer?.toString()}
        onValueChange={(i) => setSelectedAnswer(parseInt(i))}
      >
        {answers.map((a, i) => {
          const id = `answer-${i}-${reactId}`;
          return (
            <div
              key={a.label}
              className={cn(
                "flex cursor-pointer items-center gap-4 rounded-xl border p-5 transition-colors hover:bg-muted/40",
                selectedAnswer === i && "ring-2 ring-primary/30",
              )}
              onClick={() => setSelectedAnswer(i)}
            >
              <RadioGroupItem id={id} value={i.toString()} />
              <Label htmlFor={id} className="text-base leading-6 font-medium">
                {a.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </>
  );
}

export type Summary = {
  score: number;
  goodPercent: number;
  riskPercent: number;
  keyFindings: Array<Note>;
  nextSteps: Array<Note>;
};

function FindingsWithBlur({
  findings,
  summary,
  onRequestReport,
}: {
  findings: Array<Note>;
  summary: Summary;
  onRequestReport: () => void;
}) {
  const visibleCount = Math.max(1, Math.min(4, Math.ceil(findings.length / 3)));
  const visibleFindings = findings.slice(0, visibleCount);
  const blurredFindings = findings.slice(visibleCount);

  const minBlurredItems = 3;
  const hasEnoughBlurredContent = blurredFindings.length >= minBlurredItems;

  return (
    <div className="space-y-3">
      {visibleFindings.map((finding, index) => {
        const theme = severityThemes[finding.severity];
        return (
          <div
            key={`${finding.text}-${index}`}
            className={cn(
              "flex items-start gap-3 rounded-xl border px-4 py-3 text-left shadow-sm",
              theme.border,
            )}
          >
            <div
              className={cn(
                "mt-0.5 flex h-9 w-9 items-center justify-center rounded-full",
                theme.iconWrapper,
              )}
            >
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {finding.text}
              </p>
              <p
                className={cn(
                  "text-xs font-semibold tracking-wide uppercase",
                  theme.label,
                )}
              >
                {theme.title}
              </p>
            </div>
          </div>
        );
      })}

      <div className="relative">
        <div className="space-y-3 select-none" aria-hidden="true">
          {hasEnoughBlurredContent
            ? blurredFindings.map((finding, index) => {
                const theme = severityThemes[finding.severity];
                return (
                  <div
                    key={`blurred-${finding.text}-${index}`}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border px-4 py-3 text-left shadow-sm blur-[6px]",
                      theme.border,
                    )}
                  >
                    <div
                      className={cn(
                        "mt-0.5 flex h-9 w-9 items-center justify-center rounded-full",
                        theme.iconWrapper,
                      )}
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {finding.text}
                      </p>
                      <p
                        className={cn(
                          "text-xs font-semibold tracking-wide uppercase",
                          theme.label,
                        )}
                      >
                        {theme.title}
                      </p>
                    </div>
                  </div>
                );
              })
            : Array.from({ length: minBlurredItems }).map((_, index) => {
                const placeholderFinding = blurredFindings.at(index);
                const severity: Note["severity"] =
                  placeholderFinding?.severity ??
                  (index % 2 === 0 ? "warning" : "severe");
                const theme = severityThemes[severity];
                const text =
                  placeholderFinding?.text ??
                  "Additional finding details available in full report";
                return (
                  <div
                    key={`placeholder-${index}`}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border px-4 py-3 text-left shadow-sm blur-[6px]",
                      theme.border,
                    )}
                  >
                    <div
                      className={cn(
                        "mt-0.5 flex h-9 w-9 items-center justify-center rounded-full",
                        theme.iconWrapper,
                      )}
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {text}
                      </p>
                      <p
                        className={cn(
                          "text-xs font-semibold tracking-wide uppercase",
                          theme.label,
                        )}
                      >
                        {theme.title}
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="rounded-2xl border border-slate-200 bg-white/95 px-8 py-6 text-center shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95">
            <div className="mx-auto flex flex-col items-center gap-4">
              <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Mail className="size-5" strokeWidth={1.8} />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  Get Your Detailed AI Risk Report
                </h3>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Enter your details to receive a comprehensive PDF report with
                  personalized recommendations tailored to your organization.
                </p>
              </div>
              <Button
                size="default"
                className="rounded-xl px-6 py-2 text-sm font-semibold shadow-lg shadow-primary/20 transition hover:-translate-y-0.5"
                onClick={() => {
                  sessionStorage.setItem(
                    "quiz-summary",
                    JSON.stringify(summary),
                  );
                  onRequestReport();
                }}
              >
                Send Me My Report
                <ArrowRight className="ml-1 size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
